import './SCClaims.scss';
import FlexibleContentTile from '../../../Shared/Tiles/FlexibleContentTile';
import RecentActivitiesNoData, { GetNoDataTile, INoDataTileMessage } from '../../../Shared/Tiles/NoDataTile';
import LoadingScreen from '../LoadingScreen';
import NoAccessScreen from '../NoAccessScreen';
import { useEffect, useState } from 'react';
import { Table, DataTable, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { Pagination } from '@lmig/lmds-react-pagination';
import { Link } from '@lmig/lmds-react-link';
import { toTitleCase } from 'Utilities/StringFormatter';
import { FilterCard } from '../FilterCard';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { useUserData } from 'Foundation/User/UserContext';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import { useSessionStorage } from 'Utilities/UseStorage';
import { getRecentActivitiesLink, isProd } from 'Foundation/Configurations/GlobalConfiguration';
import { getClaimURL } from 'Utilities/URLHelper';
import { OverflowTooltip } from 'Foundation/Common/Tooltip';

interface IClaimsResponse {
  Claims: IClaimActivity[];
  ErrorMessage: string;
  Status: string;
}

enum ActivityType {
  Closed = 'Closed',
  Open = 'Open',
  Payment = 'Payment',
  PaymentChangedNew = 'Payment changed / payment new',
}

interface IClaimActivity {
  ActivityDate: string;
  ActivityType: string;
  AgencyCode: string;
  AgencyName: string;
  ClaimNumber: string;
  ClaimNumberName: string;
  InsuredName: string;
  PolicyNumber: string;
}

enum SortDirection {
  ascending = 'ascending',
  descending = 'descending',
  none = 'none',
}

enum SCClaimTableSortableKeys {
  claimNumber = 'policy',
  insuredName = 'insuredName',
  activity = 'activity',
}

const keyToProp: Record<SCClaimTableSortableKeys, keyof IClaimActivity> = {
  [SCClaimTableSortableKeys.claimNumber]: 'ClaimNumber',
  [SCClaimTableSortableKeys.insuredName]: 'InsuredName',
  [SCClaimTableSortableKeys.activity]: 'ActivityType',
};

interface SortDirections {
  [SCClaimTableSortableKeys.claimNumber]: SortDirection;
  [SCClaimTableSortableKeys.insuredName]: SortDirection;
  [SCClaimTableSortableKeys.activity]: SortDirection;
}

const GetEmptySortDirections = {
  [SCClaimTableSortableKeys.claimNumber]: SortDirection.none,
  [SCClaimTableSortableKeys.insuredName]: SortDirection.none,
  [SCClaimTableSortableKeys.activity]: SortDirection.none,
};

export default function Claims(props: { activeTab: number }) {
  const { userData } = useUserData();
  const resultsPerPage = 5;
  const maxResults = 49;
  const expirationMinutes = 12 * 60;
  const [page, setPage] = useState(0);
  const [claims, setClaims] = useSessionStorage<{
    activities?: IClaimActivity[] | null;
    isLoaded: boolean;
    noAccess: boolean;
  }>(
    'sc-claims-session:' + userData?.selectedStatCodeData?.SubStat,
    { isLoaded: false, noAccess: false },
    expirationMinutes,
  );
  const [filteredClaims, setFilteredClaims] = useState<IClaimActivity[]>([]);
  const [filtersSubjectCol, setSubjectColFilters] = useState<Array<ActivityType>>([]);
  const [currentSortKey, setCurrentSortKey] = useState<SCClaimTableSortableKeys>(SCClaimTableSortableKeys.insuredName);
  const [sortDirections, setSortDirections] = useState<SortDirections>({
    ...GetEmptySortDirections,
    [SCClaimTableSortableKeys.insuredName]: SortDirection.descending,
  });
  const [counts, setCounts] = useState<Array<[ActivityType, number]>>([
    [ActivityType.Closed, 0],
    [ActivityType.Open, 0],
    [ActivityType.Payment, 0],
    [ActivityType.PaymentChangedNew, 0],
  ]);

  const [noDataTile, setNoDataTile] = useState<INoDataTileMessage>({
    leftText: '<div>Loading...</div>',
    rightText: '<div>Loading...</div>',
  });

  const LMDSPagination = () => {
    const itemCount = filteredClaims?.length;
    const changeHandler = (e: any) => {
      setPage(e.page);
    };

    return (
      (itemCount > resultsPerPage && (
        <Pagination
          id="claims-pagination"
          className="recent-activities-pagination"
          withWords={false}
          itemCount={itemCount}
          onChange={changeHandler}
          showItemsPerPage={false}
          page={page}
          itemsPerPage={resultsPerPage}
        />
      )) || <></>
    );
  };

  const claimsTableSorter = (key: SCClaimTableSortableKeys) => {
    setSortDirections((prev) => ({
      ...GetEmptySortDirections,
      [key]: prev[key] === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending,
    }));
    setCurrentSortKey(key);
  };

  const toggleSubjectFilter: any = (value: ActivityType) => {
    if (filtersSubjectCol.includes(value))
      setSubjectColFilters(filtersSubjectCol.filter((filterVal: any) => filterVal !== value));
    else setSubjectColFilters([...filtersSubjectCol, value]);
  };

  const sortTable = (unsorted: IClaimActivity[]) => {
    const sorted = unsorted.sort((a: IClaimActivity, b: IClaimActivity) => {
      const key = keyToProp[currentSortKey];
      if (sortDirections[currentSortKey] === SortDirection.ascending)
        return a[key].toLowerCase().localeCompare(b[key].toLowerCase());
      else return b[key].toLowerCase().localeCompare(a[key].toLowerCase());
    });
    return sorted.slice(0, maxResults);
  };

  const areMoreRecordsThanAvailable = () => {
    return claims?.activities && filteredClaims.length < claims.activities.length && page === 9;
  };

  const filterDates = (activities: IClaimActivity[]) => {
    try {
      let dateOffset = isProd() ? -3 : -30;
      let oldestDate = new Date();
      oldestDate.setDate(oldestDate.getDate() + dateOffset);
      return activities.filter((res) => new Date(res.ActivityDate) > oldestDate);
    } catch (Exception) {}
    return activities;
  };

  const convertPaymentChangedPaymentNew = (activities: IClaimActivity[]) => {
    let old = 'PaymentChanged PaymentNew';
    return activities?.map((claim) => {
      if (claim.ActivityType === old) return { ...claim, ActivityType: ActivityType.PaymentChangedNew };
      return claim;
    });
  };

  const displayActivityType = (claim: IClaimActivity) => {
    if (claim?.ActivityType === ActivityType.PaymentChangedNew) return 'Pmt changed/new';
    else return claim?.ActivityType;
  };

  useEffect(() => {
    const fetchData = async () => {
      setClaims({ data: { isLoaded: false, noAccess: false } });
      let noAccessResponse = false;
      let response = (await SitecoreFetch(`/api/sitecore/ClaimAsync/GetClaimsRecentActivities`)) as IServerResponse;
      let result: IClaimActivity[] | null = null;
      if (response.Status.Code === HttpStatusCodes.OK) {
        let claimsResponse: IClaimsResponse | null = response.Data as IClaimsResponse;
        let fixedResponse = convertPaymentChangedPaymentNew(claimsResponse?.Claims);
        result = filterDates(fixedResponse);
      } else if (response.Status.Code === HttpStatusCodes.Forbidden) {
        noAccessResponse = true;
      }
      setClaims({ data: { activities: result, isLoaded: true, noAccess: noAccessResponse } });
    };

    try {
      if (userData?.isAgent() || userData?.isMockingAgent) {
        if (!claims?.isLoaded) fetchData();
      } else {
        setClaims({ data: { activities: null, isLoaded: true, noAccess: false } });
      }
    } catch (error) {
      console.error(error);
    }
  }, [userData?.selectedStatCodeData?.SubStat]);

  useEffect(() => {
    const getTransactionTypeCount = (t: ActivityType) => {
      return claims?.activities ? claims.activities.filter((p) => p.ActivityType == t).length : 0;
    };

    if (claims?.isLoaded) {
      if (claims.activities && claims.activities?.length > 0) {
        setCounts([
          [ActivityType.Closed, getTransactionTypeCount(ActivityType.Closed)],
          [ActivityType.Open, getTransactionTypeCount(ActivityType.Open)],
          [ActivityType.Payment, getTransactionTypeCount(ActivityType.Payment)],
          [ActivityType.PaymentChangedNew, getTransactionTypeCount(ActivityType.PaymentChangedNew)],
        ]);
        setFilteredClaims(sortTable(claims.activities));
      } else {
        GetNoDataTile('claims', setNoDataTile);
      }
    }
  }, [claims?.isLoaded, claims?.activities]);

  useEffect(() => {
    if (claims?.activities) {
      var filtered: IClaimActivity[] = claims.activities;
      if (filtersSubjectCol.length > 0) {
        filtered = claims.activities.filter((a) => filtersSubjectCol.includes(a.ActivityType as ActivityType));
      }
      setFilteredClaims(sortTable(filtered));
      setPage(0);
    }
  }, [filtersSubjectCol, sortDirections]);

  return !claims?.isLoaded ? (
    <LoadingScreen />
  ) : claims?.noAccess ? (
    <NoAccessScreen tabName="claims" />
  ) : claims?.activities && claims.activities.length !== 0 && userData ? (
    <FlexibleContentTile
      data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
      tileClass="claims-tile"
      children={[
        <>
          <div id="sc-claims-table-container" className="recent-activities-table-container">
            <div key={props.activeTab} className="sc-claims-tab-content tab-content">
              <div className="sc-claims-transactions-graphic">
                <div className="filterCard">
                  <FilterCard activeFilters={filtersSubjectCol} list={counts} filterFunction={toggleSubjectFilter} />
                </div>
              </div>
              <div className="design-system-data-table-container">
                {filteredClaims.length !== 0 ? (
                  <>
                    <div className="design-system-data-table">
                      <DataTable>
                        <Table className="design-system-table" id="claims-design-system-table" spacing="micro">
                          <TableHeader id="sc-claims-table-header">
                            <TableRow>
                              <TableCell
                                type="colHead"
                                className="claim-number-header"
                                isSortable
                                sortDirection={sortDirections.policy}
                                onClick={() => claimsTableSorter(SCClaimTableSortableKeys.claimNumber)}
                              >
                                Claim number
                              </TableCell>
                              <TableCell
                                type="colHead"
                                className="insured-name-header"
                                isSortable
                                sortDirection={sortDirections.insuredName}
                                onClick={() => claimsTableSorter(SCClaimTableSortableKeys.insuredName)}
                              >
                                Insured name
                              </TableCell>
                              <TableCell
                                type="colHead"
                                className="activity-type-header"
                                isSortable
                                sortDirection={sortDirections.activity}
                                onClick={() => claimsTableSorter(SCClaimTableSortableKeys.activity)}
                              >
                                Activity
                              </TableCell>
                            </TableRow>
                          </TableHeader>
                          <TableBody id="sc-claims-table-body">
                            {filteredClaims.length ? (
                              filteredClaims
                                .slice(
                                  page * resultsPerPage,
                                  Math.min(page * resultsPerPage + resultsPerPage, filteredClaims?.length),
                                )
                                .map((data) => (
                                  <TableRow>
                                    <TableCell className="claim-number-cell">
                                      <Link
                                        target="_blank"
                                        href={getClaimURL(data.ClaimNumber, userData, data.AgencyCode)}
                                      >
                                        {data.ClaimNumber}
                                      </Link>
                                    </TableCell>
                                    <TableCell className="insured-name-cell">
                                      <OverflowTooltip text={toTitleCase(data.InsuredName) ?? ''}>
                                        <div>{toTitleCase(data.InsuredName) ?? ''}</div>
                                      </OverflowTooltip>
                                    </TableCell>
                                    <TableCell className="activity-type-cell">
                                      <div>{displayActivityType(data)}</div>
                                    </TableCell>
                                  </TableRow>
                                ))
                            ) : (
                              <></>
                            )}
                          </TableBody>
                        </Table>
                      </DataTable>
                    </div>

                    {areMoreRecordsThanAvailable() && (
                      <div className="view-all-recent-activities">
                        <Link
                          target="_blank"
                          href={getRecentActivitiesLink()}
                          variant="standalone"
                          className="portal-tile-v2-header-link"
                        >
                          View all recent activities
                        </Link>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-data-found">
                    <div className="no-data-found-inner"></div>
                    No claims activities found. Try another filter
                  </div>
                )}
              </div>
            </div>
            <LMDSPagination />
          </div>
        </>,
      ]}
    ></FlexibleContentTile>
  ) : (
    <RecentActivitiesNoData
      messageLeft={noDataTile?.leftText}
      messageRight={noDataTile?.rightText}
    ></RecentActivitiesNoData>
  );
}
